// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import CircularProgress from '@mui/material/CircularProgress'
import CommentIcon from '@mui/icons-material/Chat'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import classNames from 'classnames'
import { orderBy } from 'lodash'
import moment from 'moment'
import React from 'react'
import { i18n } from '../index'
import TaskListCheckModal from './TaskListCheckModal'
import TaskListEditForm from './TaskListEditForm'
import type { Assignment } from './data'
import { getAssignmentSubjectLabel } from './data'
import css from './taskListTable.css'
type SortingKeys = 'deadline' | 'subject' | 'assignee.name'
type Sorting = {
  key: SortingKeys
  direction: 'asc' | 'desc' | null
}
type Props = {
  items: Array<any>
  isLoading: boolean
  canEdit: boolean
  onItemSaved?: (...args: Array<any>) => any
}
type State = {
  itemToView: string | null | undefined
  itemToEdit: string | null | undefined
  itemToCheck: any | null | undefined
  sorting: Sorting
}

const ItemToView = ({ item }: { item: any }) => {
  let allAssignees = []
  if (item.assignee) {
    allAssignees.push(item.assignee)
  }
  if (item.assignees) {
    allAssignees = allAssignees.concat(item.assignees)
  }
  const assigneeNames = allAssignees.map((assignee) => assignee.name).join(', ')
  return (
    <div className={css.itemToView}>
      <p className={css.label}>Tehtävä</p>
      <p className={css.value}>{item.description}</p>

      <p className={css.label}>Kommentit</p>
      <p className={css.value}>{item.comment}</p>

      <p className={css.label}>Vastuuhenkilöt</p>
      <p className={css.value}>{assigneeNames}</p>
    </div>
  )
}

class TaskListTable extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      itemToView: null,
      itemToEdit: null,
      itemToCheck: null,
      sorting: {
        key: 'deadline',
        direction: 'asc',
      },
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.items !== this.props.items) {
      this.setState({
        items: this.props.items,
      })
    }
  }

  onItemEdit = (assignment: Assignment) => {
    this.setState({
      itemToView: null,
      itemToEdit: assignment.id,
    })
  }
  onItemView = (assignment: Assignment) => {
    this.setState({
      itemToEdit: null,
      itemToView: this.state.itemToView === assignment.id ? null : assignment.id,
    })
  }
  onEditSave = () => {
    if (this.props.onItemSaved) {
      this.props.onItemSaved()
    }
    this.setState({
      itemToView: null,
      itemToEdit: null,
    })
  }
  onEditClose = () => {
    this.setState({
      itemToView: null,
      itemToEdit: null,
    })
  }
  onItemCheckedClick = (assignment: Assignment) => {
    if (assignment.status === 'Done') {
      return
    }

    this.setState({
      itemToCheck: assignment,
    })
  }
  renderDeadline = (assignment: Assignment) => {
    const deadline = moment(assignment.deadline)
    const ready = moment(assignment.doneAt)
    const now = moment()
    return (
      <span
        className={classNames(null, {
          [css.negative]: (assignment.doneAt && deadline.diff(ready, 'days') < 0) || deadline.diff(now, 'days') < 0,
        })}
      >
        {deadline.format('D.M.Y')}
      </span>
    )
  }
  renderReady = (assignment: Assignment) => {
    if (!assignment.doneAt) {
      return null
    }

    const deadline = moment(assignment.deadline)
    const ready = moment(assignment.doneAt)
    return (
      <span
        className={classNames(null, {
          [css.positive]: deadline.diff(ready, 'days') >= 0,
        })}
      >
        {ready.format('D.M.Y')}
      </span>
    )
  }
  getSortedItems = (): Array<any> => {
    const { items } = this.props
    const { sorting } = this.state

    if (!sorting.direction) {
      return items
    }

    return orderBy(items, [sorting.key], [sorting.direction])
  }
  toggleSorting = (key: SortingKeys) => {
    const { sorting } = this.state

    if (sorting.key !== key) {
      this.setState({
        sorting: {
          key,
          direction: 'asc',
        },
      })
    } else {
      const direction = !sorting.direction ? 'asc' : sorting.direction === 'asc' ? 'desc' : null
      this.setState({
        sorting: {
          key,
          direction,
        },
      })
    }
  }
  renderItems = () => {
    const { canEdit, items } = this.props
    const { itemToView, itemToEdit } = this.state

    if (!items.length) {
      return (
        <tr>
          <td colSpan='8' className={css.emptyText}>
            {i18n.t('taskList.table.emptyText')}
          </td>
        </tr>
      )
    }

    return this.getSortedItems().map((item, i) => {
      const itemAssignees = []
      if (item.assignee) {
        itemAssignees.push(item.assignee)
      }
      if (item.assignees) {
        itemAssignees.push(...item.assignees)
      }
      return (
        <React.Fragment key={i}>
          <tr className={css.itemRow}>
            <td>
              <a
                className={classNames(css.arrow, {
                  [css.open]: item.id === itemToView || item.id === itemToEdit,
                })}
                onClick={() => this.onItemView(item)}
              >
                <KeyboardArrowRightIcon />
              </a>
            </td>
            <td>
              <div className={css.subject}>{getAssignmentSubjectLabel(item.subject)}</div>
            </td>
            <td>
              <div className={css.description}>
                {item.description}
                {!!item.comment && (
                  <div className={css.commentIcon}>
                    <CommentIcon />
                  </div>
                )}
              </div>
            </td>
            <td>
              <div className={css.assignee}>{itemAssignees.map((assignee) => assignee.name).join(', ')}</div>
            </td>
            <td>{this.renderDeadline(item)}</td>
            <td>{this.renderReady(item)}</td>
            <td>
              <div className={css.action}>
                {canEdit && item.status !== 'Done' && (
                  <a className={css.editButton} onClick={() => this.onItemEdit(item)}>
                    <EditIcon />
                  </a>
                )}
              </div>
            </td>
            <td>
              <div className={css.action}>
                {canEdit && (
                  <a
                    className={classNames(css.checkButton, {
                      [css.checked]: item.status === 'Done',
                    })}
                    onClick={() => this.onItemCheckedClick(item)}
                  >
                    <CheckCircleIcon />
                  </a>
                )}
              </div>
            </td>
          </tr>
          {itemToView === item.id && (
            <tr className={css.itemToViewRow}>
              <td />
              <td colSpan='7'>
                <ItemToView item={item} />
              </td>
            </tr>
          )}
          {itemToEdit === item.id && (
            <tr className={css.itemToEditRow}>
              <td colSpan='8'>
                <TaskListEditForm assignment={item} onClose={this.onEditClose} onSave={this.onEditSave} />
              </td>
            </tr>
          )}
        </React.Fragment>
      )
    })
  }
  renderSortingIcon = (key: SortingKeys) => {
    const { sorting } = this.state

    if (key === sorting.key) {
      if (sorting.direction) {
        return <span className={`${css.caret} ${css[sorting.direction]} ${css.solo}`} />
      }
    }

    return (
      <React.Fragment>
        <span
          className={`${css.caret} ${css.asc}`}
          style={{
            marginBottom: '3px',
          }}
        />
        <span className={`${css.caret} ${css.desc}`} />
      </React.Fragment>
    )
  }

  render() {
    const { isLoading, canEdit } = this.props
    return (
      <div
        className={classNames(css.component, {
          [css.isLoading]: isLoading,
        })}
      >
        <table className={css.table}>
          <thead>
            <tr>
              <th className={css.firstCol} />
              <th className={css.subjectCol}>
                <div className={css.headerContainer} onClick={() => this.toggleSorting('subject')}>
                  {i18n.t('taskList.table.subjectHeader')}
                  <div className={css.headerCaretContainer}>{this.renderSortingIcon('subject')}</div>
                </div>
              </th>
              <th className={css.descriptionCol}>{i18n.t('taskList.table.descriptionHeader')}</th>
              <th className={css.assigneeCol}>
                <div className={css.headerContainer} onClick={() => this.toggleSorting('assignee.name')}>
                  {i18n.t('taskList.table.assigneeHeader')}
                  <div className={css.headerCaretContainer}>{this.renderSortingIcon('assignee.name')}</div>
                </div>
              </th>
              <th className={css.deadlineCol}>
                <div className={css.headerContainer} onClick={() => this.toggleSorting('deadline')}>
                  {i18n.t('taskList.table.deadlineHeader')}
                  <div className={css.headerCaretContainer}>{this.renderSortingIcon('deadline')}</div>
                </div>
              </th>
              <th className={css.readyCol}>{i18n.t('taskList.table.readyHeader')}</th>
              <th className={css.actionHeader}>{canEdit && i18n.t('taskList.table.editHeader')}</th>
              <th className={css.actionHeader}>{canEdit && i18n.t('taskList.table.checkHeader')}</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && this.renderItems()}
            {isLoading && (
              <tr className={css.loadingIndicator}>
                <td colSpan='8'>
                  <CircularProgress color='primary' />
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {this.state.itemToCheck && (
          <TaskListCheckModal
            assignment={this.state.itemToCheck}
            open={!!this.state.itemToCheck}
            onClose={() =>
              this.setState({
                itemToCheck: null,
              })
            }
          />
        )}
      </div>
    )
  }
}

export default TaskListTable
